import React, { createContext, useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { app_sso } from '../firebase'
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
} from 'firebase/auth'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const baseURL = process.env.REACT_APP_BACKEND_BASE_URL
  const cookies = new Cookies()
  const queryClient = useQueryClient()
  const [isLoggedOut, setIsLoggedOut] = useState(() => !cookies.get('user')) // Track logout state
  const [error, setError] = useState(null)

  // Function to prepare headers for fetch requests
  const prepareHeaders = () => {
    let headers = new Headers()
    headers.append('Content-Type', 'application/json')
    headers.append('Accept', 'application/json')
    headers.append('X-MMUF-API-Version', '1.0.0')
    headers.append('X-MMUF-Client', 'MMUF/1.0.0')
    return headers
  }

  // Fetch current user using useQuery
  const {
    data: currentAuthUser,
    isError,
    isLoading: isCurrenAuthUserLoading,
  } = useQuery({
    queryKey: ['currentAuthUser'],
    queryFn: () => {
      return fetch(`${baseURL}/account`, {
        method: 'GET',
        headers: prepareHeaders(),
        credentials: 'include',
      }).then(res => {
        if (res.ok) return res.json()
        if (res.status === 401) {
          userLoggedOut()
        } else if (res.status === 403) {
          setError({ message: "This user doesn't have access to the website" })
          throw new Error("This user doesn't have access to the website")
        } else {
          setError({ message: 'Failed to fetch data with code: ' + res.status })
          throw new Error('Failed to fetch data with code: ' + res.status)
        }
      })
    },
    enabled: !isLoggedOut,
    retry: false,
  })
  // Handle side effects when currentAuthUser or isError changes
  useEffect(() => {
    if (currentAuthUser) {
      setIsLoggedOut(false)
      cookies.set('user', currentAuthUser, { path: '/' })
      if (pathname.includes('/login') || pathname === '/reset-password') {
        navigate('/', { replace: true })
      }
    }

    if (isError) {
      cookies.remove('user', { path: '/' })
      setIsLoggedOut(true)
      navigate('/login')
    }
  }, [currentAuthUser, isError, pathname, navigate])

  // useMutation for login
  const loginMutation = useMutation({
    mutationFn: googleJWT =>
      fetch(`${baseURL}/login`, {
        method: 'POST',
        headers: prepareHeaders(),
        body: JSON.stringify(googleJWT),
        credentials: 'include',
      }).then(res => {
        if (res.ok) return res.json()
        if (res.status === 401) {
          userLoggedOut()
        } else if (res.status === 403) {
          throw new Error("This user doesn't have access to the website")
        } else {
          throw new Error('Failed to fetch data with code: ' + res.status)
        }
      }),
    onSuccess: data => {
      queryClient.setQueryData(['currentAuthUser'], data)
      setIsLoggedOut(false) // Reset logout state on successful login
    },
    onError: error => {
      setError({ message: error.message })
    },
  })

  // useMutation for logout
  const logoutMutation = useMutation({
    mutationFn: () =>
      fetch(`${baseURL}/logout`, {
        method: 'POST',
        headers: prepareHeaders(),
        body: JSON.stringify({ logout: 'logout' }),
        credentials: 'include',
      }).then(res => {
        if (!res.ok) throw new Error('Logout filed with code: ' + res.status)
      }),
    onSuccess: () => {
      userLoggedOut()
    },
  })

  const loginWithEmailMutation = useMutation({
    mutationKey: ['loginWithEmailMutation'],
    mutationFn: accessToken =>
      fetch(`${baseURL}/login?idp=1`, {
        method: 'POST',
        headers: prepareHeaders(),
        body: JSON.stringify({ accessToken: accessToken }),
        credentials: 'include',
      }).then(res => {
        if (res.ok) return res.json()
        if (res.status === 401) {
          throw new Error("You haven't been invited")
        } else if (res.status === 403) {
          throw new Error("This user doesn't have access to the website")
        } else {
          throw new Error('Failed to fetch data with code: ' + res.status)
        }
      }),
    onSuccess: userCredential => {
      if (userCredential) {
        queryClient.setQueryData(['currentAuthUser'], userCredential)
        setIsLoggedOut(false)
      }
    },
  })

  const loginWithGoogleMutation = useMutation({
    mutationKey: ['loginWithGoogle'],
    mutationFn: async () => {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account',
      })
      const auth = getAuth(app_sso)
      const userCredential = await signInWithPopup(auth, provider)

      const idToken = await userCredential.user.getIdToken(true)

      const response = await fetch(`${baseURL}/login?idp=1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: idToken }),
        credentials: 'include',
      })

      if (response.ok) return response.json()

      if (response.status === 401) {
        throw new Error("You haven't been invited")
      }
      if (response.status === 403) {
        throw new Error("This user doesn't have access to the website")
      }
      throw new Error('Unknown error: ' + response.status)
    },
    onSuccess: userData => {
      queryClient.setQueryData(['currentAuthUser'], userData)
      setIsLoggedOut(false)
    },
  })

  const loginWithSAMLMutation = useMutation({
    mutationKey: ['loginWithSAML'],
    mutationFn: async providerId => {
      const provider = new SAMLAuthProvider(providerId)
      const auth = getAuth(app_sso)
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken(true)
      const response = await fetch(`${baseURL}/login?idp=1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: idToken }),
        credentials: 'include',
      })

      if (response.ok) return response.json()

      if (response.status === 401) {
        throw new Error("You haven't been invited")
      }
      if (response.status === 403) {
        throw new Error("This user doesn't have access to the website")
      }
      throw new Error('Unknown error: ' + response.status)
    },
    onSuccess: userData => {
      queryClient.setQueryData(['currentAuthUser'], userData)
      setIsLoggedOut(false)
    },
  })

  const loginWithOAuthMutation = useMutation({
    mutationKey: ['loginWithOAuth'],
    mutationFn: async providerId => {
      const provider = new OAuthProvider(providerId)
      const auth = getAuth(app_sso)
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken(true)

      const response = await fetch(`${baseURL}/login?idp=1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: idToken }),
        credentials: 'include',
      })

      if (response.ok) return response.json()

      if (response.status === 401) {
        throw new Error("You haven't been invited")
      }
      if (response.status === 403) {
        throw new Error("This user doesn't have access to the website")
      }
      throw new Error('Unknown error: ' + response.status)
    },
    onSuccess: userData => {
      queryClient.setQueryData(['currentAuthUser'], userData)
      setIsLoggedOut(false)
    },
  })

  function userLoggedOut() {
    cookies.remove('user', { path: '/' })
    queryClient.clear()
    setIsLoggedOut(true) // Set logout state to false

    // after getting 401 status code error we should stay in place
    if (pathname !== '/login/invite' && pathname !== '/reset-password')
      navigate('/login')
  }

  const handleGoogle = async response => {
    let googleJWT = { accessToken: response.credential }
    return googleJWT
  }

  const refreshCurrentUser = async () => {
    await queryClient.invalidateQueries(['currentAuthUser'])
  }

  // Force refresh user data on every page load (get permissions changes)
  useEffect(() => {
    refreshCurrentUser()
  }, []) 

  const value = {
    getCurrentUser: () => currentAuthUser || cookies.get('user'),
    login: googleJWT => loginMutation.mutate(googleJWT),
    logout: () => logoutMutation.mutate(),
    loginWithEmail: accessToken =>
      loginWithEmailMutation.mutateAsync(accessToken),
    loginWithGoogle: () => loginWithGoogleMutation.mutateAsync(),
    loginWithSAML: providerId => loginWithSAMLMutation.mutateAsync(providerId),
    loginWithOAuth: providerId =>
      loginWithOAuthMutation.mutateAsync(providerId),
    handleGoogle,
    currentAuthUser,
    error,
    setError,
    isCurrenAuthUserLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
